import type { GccCurrentLocation, GccOwnership, GccValueCase, GccValueFigures, GccValueLedgerLine, GccWave } from './types'
import { GCC_OWNERSHIPS } from './sourcing'

// Zod-free so the UI can import it without pulling in the schema layer.

/** Where work can sit today (WI 2326): onshore only, since the GCC does not exist yet. */
export const GCC_CURRENT_LOCATIONS: readonly GccCurrentLocation[] = ['HQ', 'Markets']

/** Current location of a role with no valid one: the conservative assumption that it sits at HQ. */
export const GCC_DEFAULT_CURRENT_LOCATION: GccCurrentLocation = 'HQ'

export function isGccCurrentLocation(value: unknown): value is GccCurrentLocation {
  return typeof value === 'string' && (GCC_CURRENT_LOCATIONS as readonly string[]).includes(value)
}

/** Steps of the Step 4 traceability chain, in waterfall order (WI 2326). */
export const GCC_VALUE_WATERFALL_STEPS = [
  { key: 'currentFte', label: 'Current FTE' },
  { key: 'aiReleasedFte', label: 'AI released FTE' },
  { key: 'residualFte', label: 'Residual FTE' },
  { key: 'sourcingSplit', label: 'Sourcing split' },
  { key: 'futureFte', label: 'Future workforce' },
  { key: 'valueCase', label: 'Net value case' },
] as const

type WaveFields = Pick<GccValueLedgerLine, 'transformWave' | 'migrationWave'>

/** Wave a task's AI saving starts in: its transform wave (Wave 3 if it has none, the conservative timing). */
export function aiBenefitWave(line: WaveFields): GccWave {
  return line.transformWave ?? 3
}

/** Wave a task's location saving starts in: its migration wave (Wave 3 if it has none). */
export function locationBenefitWave(line: WaveFields): GccWave {
  return line.migrationWave ?? 3
}

/** The earliest wave in which a task saves anything; null when it saves nothing. */
export function firstBenefitWave(line: WaveFields & Pick<GccValueLedgerLine, 'aiSavingUSD' | 'locationSavingUSD'>): GccWave | null {
  const waves: GccWave[] = []
  if (line.aiSavingUSD !== 0) waves.push(aiBenefitWave(line))
  if (line.locationSavingUSD !== 0) waves.push(locationBenefitWave(line))
  return waves.length > 0 ? (Math.min(...waves) as GccWave) : null
}

/** Cents, so FTE sums compare exactly */
const cents = (fte: number) => Math.round(fte * 100)
const sumCents = (values: number[]) => values.reduce((s, v) => s + cents(v), 0)

function figureMismatches(label: string, f: GccValueFigures): string[] {
  const out: string[] = []
  if (f.aiSavingUSD + f.locationSavingUSD !== f.totalSavingUSD) out.push(`${label}: AI + location saving ≠ total saving`)
  if (f.currentCostUSD - f.endStateCostUSD !== f.totalSavingUSD) out.push(`${label}: current − end-state cost ≠ total saving`)
  return out
}

const FIGURE_KEYS: ReadonlyArray<keyof GccValueFigures> = [
  'currentCostUSD', 'aiSavingUSD', 'locationSavingUSD', 'totalSavingUSD', 'endStateCostUSD',
]

/**
 * Every place the value case fails to reconcile. Always empty for results the
 * pipeline calculated; Pipeline Traceability shows it as a check.
 *
 * - each task, capability and the totals: AI + location = total = current − end-state cost
 * - each capability: current − AI released = residual FTE = Σ sourcing split = Σ future FTE,
 *   and its costs are the sum of its tasks
 * - the totals are the sum of the capabilities, and the run-rate after the last wave is the total saving
 */
export function findValueCaseMismatches(vc: GccValueCase): string[] {
  const out: string[] = []
  for (const t of vc.tasks) out.push(...figureMismatches(`Task ${t.taskId}`, t))

  for (const c of vc.capabilities) {
    const label = `${c.functionScope} / ${c.capabilityName}`
    out.push(...figureMismatches(label, c))
    if (cents(c.currentFte) - cents(c.aiReleasedFte) !== cents(c.residualFte)) out.push(`${label}: current − AI released FTE ≠ residual FTE`)
    if (sumCents(c.sourcingSplit.map((a) => a.fte)) !== cents(c.residualFte)) out.push(`${label}: sourcing split ≠ residual FTE`)
    if (sumCents(GCC_OWNERSHIPS.map((o: GccOwnership) => c.futureFteByOwnership[o])) !== cents(c.residualFte)) {
      out.push(`${label}: future workforce ≠ residual FTE`)
    }
    const lines = vc.tasks.filter((t) => t.capabilityName === c.capabilityName && t.functionScope === c.functionScope)
    for (const k of FIGURE_KEYS) {
      if (lines.reduce((s, t) => s + t[k], 0) !== c[k]) out.push(`${label}: ${k} ≠ sum of its tasks`)
    }
  }

  out.push(...figureMismatches('Totals', vc.totals))
  for (const k of FIGURE_KEYS) {
    if (vc.capabilities.reduce((s, c) => s + c[k], 0) !== vc.totals[k]) out.push(`Totals: ${k} ≠ sum of capabilities`)
  }
  const fteKeys = ['currentFte', 'aiReleasedFte', 'residualFte'] as const
  for (const k of fteKeys) {
    if (sumCents(vc.capabilities.map((c) => c[k])) !== cents(vc.totals[k])) out.push(`Totals: ${k} ≠ sum of capabilities`)
  }
  const last = vc.waves[vc.waves.length - 1]
  if (last && last.cumulativeSavingUSD !== vc.totals.totalSavingUSD) out.push('Waves: run-rate after the last wave ≠ total saving')
  return out
}
